import {
    AmountType,
    DownDeposit,
    Media,
    Merchandise,
    MerchandiseCatalogItem,
    MerchandiseFulfillmentPolicy,
    MerchandiseFulfillmentType,
    MerchandiseInventoryPolicy,
    MerchandiseRefundPolicy,
    MerchandiseSource,
    MerchandiseType,
    OversellPolicy,
    PaymentMethod,
    ProductObject,
    PurchaseItemObject,
    PurchaseStatus,
    RevenueRecognitionPolicy,
    UserDetailsRequirement,
    VariantGroup,
    VariantType,
    WarePreference,
} from "types";
import { api } from "@/api/api";
import { hasVariantsEnabled, isInventoryTracked } from "@/models/Merchandise";
import { getSeller } from "@/utils/getSeller";

export interface MerchandiseInventoryFormValues {
    trackQuantity: boolean;
    quantity: number;
    notifyLowInventory: boolean;
    lowInventoryThreshold?: number;
    oversellPolicy: OversellPolicy;
    hasVariants: boolean;
    variantGroups: VariantGroup[];
    sku: string;
    barcode: string;
    price: number;
}

export interface MerchandiseFulfillmentFormValues {
    type: MerchandiseType;
    localPickup: boolean;
    shipping: boolean;
    revenueRecognitionPolicy: RevenueRecognitionPolicy;
    fulfillmentPolicy: MerchandiseFulfillmentPolicy;
    refundPolicy: MerchandiseRefundPolicy;
    customerNamePreference: UserDetailsRequirement;
    terms: string;
}

export interface MerchandiseDescriptionFormValues {
    id?: string;
    name: string;
    excerpt: string;
    collections?: string[];
    attachments?: any[];
    object: ProductObject;
}

export interface MerchandisePricingFormValues {
    downDeposit: DownDeposit | null;
    paymentMethod: PaymentMethod;
    allowedSources: string[];
}

export interface UnfulfilledMerchandisePurchasesPayload {
    limit: number;
    seller: string;
    "items.product[in]": string;
    "items.object[in]": PurchaseItemObject.WARE;
    status: PurchaseStatus.COMMITTED;
    "items.fulfillmentStatus[in]": "partially_fulfilled,pending_fulfillment";
}

export const getCatalogItemPatchPayload = (price: number) => {
    return {
        pricePerUnit: {
            price,
            priceType: AmountType.ABSOLUTE,
        },
    };
};

export const fetchPendingUnfulfilledMerchandisePurchases = async (sellerId: string, merchandiseId: string) => {
    const params = getPendingUnfulfilledMerchandisePurchasesPayload(sellerId, merchandiseId);
    const { data } = await api.get<{ data: unknown[]; paging?: { next?: string | null } }>("/purchases", { params });
    return data;
};

export const getPendingUnfulfilledMerchandisePurchasesPayload = (
    sellerId: string,
    merchandiseId: string,
): UnfulfilledMerchandisePurchasesPayload => {
    return {
        limit: 100,
        seller: sellerId,
        "items.product[in]": merchandiseId,
        "items.object[in]": PurchaseItemObject.WARE,
        status: PurchaseStatus.COMMITTED,
        "items.fulfillmentStatus[in]": "partially_fulfilled,pending_fulfillment",
    };
};

export const getMerchandiseInventoryPayload = (
    values: {
        trackQuantity: boolean;
        hasVariants: boolean;
        variantGroups: VariantGroup[];
    },
    existingWarePreference?: WarePreference,
) => {
    const configuredInventoryPolicy = values.trackQuantity
        ? MerchandiseInventoryPolicy.TRACKED
        : MerchandiseInventoryPolicy.UNLIMITED;
    const nextInventoryPolicy =
        configuredInventoryPolicy !== existingWarePreference?.inventoryPolicy ? configuredInventoryPolicy : undefined;

    const groups = values.hasVariants
        ? values.variantGroups.map((group, groupIndex) => ({
              id: group.id,
              name: group.name,
              sequence: groupIndex,
              options: group.options.map((option, optionIndex) => ({
                  id: option.id,
                  name: option.name,
                  sequence: optionIndex,
              })),
          }))
        : undefined;

    return {
        ...(values.hasVariants ? { variantGroups: groups } : {}),
        warePreference: {
            ...existingWarePreference,
            variantType: values.hasVariants ? VariantType.MULTIPLE : VariantType.SINGLE,
            inventoryPolicy: nextInventoryPolicy,
        },
    };
};

const getVariantGroupIdentity = (group: VariantGroup): string => {
    return group.id ?? group.name.trim();
};

const getVariantOptionIdentity = (option: { id?: string; name: string }): string => {
    return option.id ?? option.name.trim();
};

export const hasVariantGroupStructureChanged = (
    initialVariantGroups: VariantGroup[],
    currentVariantGroups: VariantGroup[],
): boolean => {
    if (initialVariantGroups.length !== currentVariantGroups.length) {
        return true;
    }

    const currentGroupsByIdentity = new Map(
        currentVariantGroups.map((group) => [getVariantGroupIdentity(group), group]),
    );

    for (const initialGroup of initialVariantGroups) {
        const currentGroup = currentGroupsByIdentity.get(getVariantGroupIdentity(initialGroup));

        // Existing group removed.
        if (!currentGroup) {
            return true;
        }

        const currentOptionIdentities = new Set(currentGroup.options.map((option) => getVariantOptionIdentity(option)));

        // Existing option removed from an existing group.
        const hasRemovedOption = initialGroup.options.some(
            (option) => !currentOptionIdentities.has(getVariantOptionIdentity(option)),
        );

        if (hasRemovedOption) {
            return true;
        }
    }

    return false;
};

export const getMerchandiseFulfillmentPayload = (
    values: {
        type: MerchandiseType;
        localPickup: boolean;
        shipping: boolean;
        revenueRecognitionPolicy: RevenueRecognitionPolicy;
        fulfillmentPolicy: MerchandiseFulfillmentPolicy;
        refundPolicy: MerchandiseRefundPolicy;
        customerNamePreference: UserDetailsRequirement;
        terms: string;
    },
    existingWarePreference?: WarePreference,
) => {
    const fulfillmentType = [
        values.localPickup ? MerchandiseFulfillmentType.IN_STORE : null,
        values.shipping ? MerchandiseFulfillmentType.SHIPPING : null,
    ].filter(Boolean) as MerchandiseFulfillmentType[];

    return {
        type: values.type,
        terms: values.terms,
        customerNamePreference: values.customerNamePreference,
        warePreference: {
            ...existingWarePreference,
            fulfillmentType,
            revenueRecognitionPolicy: values.revenueRecognitionPolicy,
            fulfillmentPolicy: values.fulfillmentPolicy,
            refundPolicy: values.refundPolicy,
        },
    };
};

export const getMerchandiseDescriptionPayload = (values: MerchandiseDescriptionFormValues) => {
    const sellerId = getSeller().id;

    return {
        ...values,
        seller: { id: sellerId },
    };
};

export const getInventoryInitialValues = (merchandise?: Merchandise): MerchandiseInventoryFormValues => {
    const variantGroups = merchandise?.variantGroups ?? [];

    return {
        trackQuantity: merchandise ? isInventoryTracked(merchandise) : false,
        quantity: 0,
        notifyLowInventory: false,
        lowInventoryThreshold: undefined,
        oversellPolicy: OversellPolicy.REQUIRE_AVAILABILITY,
        hasVariants: merchandise ? hasVariantsEnabled(merchandise) : false,
        variantGroups,
        sku: "",
        barcode: "",
        price: 0,
    };
};

export interface MerchandiseItemsFormValues {
    sku: string;
    barcode: string;
    price: number | undefined;
    trackQuantity: boolean;
    quantity: number;
    variantItems: Record<string, { sku: string; barcode: string; price: number | null }>;
}

export const getItemsInitialValues = (catalogItem?: MerchandiseCatalogItem): MerchandiseItemsFormValues => ({
    sku: catalogItem?.sku ?? "",
    barcode: catalogItem?.barcode ?? "",
    price: catalogItem?.prices?.perUnit?.price ?? catalogItem?.price,
    trackQuantity: false,
    quantity: 0,
    variantItems: {},
});

export const getFulfillmentInitialValues = (merchandise?: Merchandise): MerchandiseFulfillmentFormValues => {
    return {
        type: merchandise?.type ?? MerchandiseType.PHYSICAL,
        localPickup:
            merchandise?.warePreference?.fulfillmentType?.includes(MerchandiseFulfillmentType.IN_STORE) ?? true,
        shipping: merchandise?.warePreference?.fulfillmentType?.includes(MerchandiseFulfillmentType.SHIPPING) ?? false,
        revenueRecognitionPolicy:
            merchandise?.warePreference?.revenueRecognitionPolicy ?? RevenueRecognitionPolicy.FULFILLMENT,
        fulfillmentPolicy: merchandise?.warePreference?.fulfillmentPolicy ?? MerchandiseFulfillmentPolicy.MANUAL,
        refundPolicy: merchandise?.warePreference?.refundPolicy ?? MerchandiseRefundPolicy.REFUND_ALLOWED,
        customerNamePreference: merchandise?.customerNamePreference ?? UserDetailsRequirement.NOT_COLLECTED,
        terms: merchandise?.terms ?? "",
    };
};

export const getDescriptionInitialValues = (
    merchandise?: Merchandise,
    addedCollectionPaths: string[] = [],
): MerchandiseDescriptionFormValues => {
    return {
        name: merchandise?.name ?? "",
        excerpt: merchandise?.excerpt ?? "",
        collections: addedCollectionPaths,
        attachments: merchandise?.attachments ?? [],
        object: ProductObject.WARE,
    };
};

export const getPricingInitialValues = (merchandise?: Merchandise): MerchandisePricingFormValues => {
    const purchaseSourceValues = Object.values(MerchandiseSource);
    return {
        downDeposit: merchandise?.downDeposit ?? null,
        paymentMethod: merchandise?.paymentMethod ?? PaymentMethod.CASH,
        allowedSources: merchandise?.allowedSources ?? [...purchaseSourceValues],
    };
};

export const getUploadImageInitialValues = (catalogItem: MerchandiseCatalogItem, media?: Media) => {
    return {
        caption: media ? media.caption ?? "" : "",
        isPrimaryPhoto: media?.id === catalogItem.photo?.id,
    };
};
